import { useAppStore } from '@/store/useAppStore';
import { READINESS_DOMAIN, WELLNESS_DOMAINS_DISPLAY_LABELS, WELLNESS_DOMAINS_LIST } from '@/constants';
import { LOCAL_US_STATES } from '@/constants/stringVars';
import { primary } from '@/theme/colors/palettes';
import { Box, Button, HStack, Image, Link, Text, VStack } from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { ContentFocusValue } from './ContentFocusTile';
import type { CreatorApplicationPreviewData } from '@/store/useCreatorApplicationStore';
import svgFiles from '@/assets/svg/svgFiles';
import { useAuthContext } from '@/context/auth/useAuthContext';
import roles from '@/constants/roles';
import { buildCreatorSocialLinks } from './creatorSocialLinks';
import type { CreatorSocialPlatform } from './creatorSocialLinks';
import externalAppLogos from '@/assets/img/png/externalAppLogos';

const contentFocusOptions: Array<{ label: string; value: ContentFocusValue }> = [
  ...WELLNESS_DOMAINS_LIST.map((domain) => ({
    label: WELLNESS_DOMAINS_DISPLAY_LABELS[domain],
    value: domain,
  })),
  { label: 'Readiness', value: READINESS_DOMAIN },
];

const getContentFocusLabel = (value: ContentFocusValue) =>
  contentFocusOptions.find((option) => option.value === value)?.label ?? value;

const getStateLabel = (stateCode: string) =>
  LOCAL_US_STATES.find((USState) => USState.code === stateCode)?.label ?? stateCode;

const socialPlatformLabels: Record<CreatorSocialPlatform, string> = {
  x: 'X',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  quora: 'Quora',
};

const CreatorApplicationProfilePreview = ({ data }: { data: CreatorApplicationPreviewData }) => {
  const navigate = useNavigate();
  const memberPhoto = useAppStore((state) => state.user?.user_metadata.profilePicture);
  const { isAuthenticated, user } = useAuthContext();
  const selectedContentFocusLabels = data.contentFocus.map(getContentFocusLabel).join(', ');
  const socialLinks = buildCreatorSocialLinks(data.socialMediaHandles);
  const locationParts = [data.city, data.state ? getStateLabel(data.state) : '', data.zipCode]
    .map((part) => part?.trim())
    .filter(Boolean);
  const authenticatedUserName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  const applicantName = [data.applicantName?.firstName, data.applicantName?.lastName]
    .map((namePart) => namePart?.trim())
    .filter(Boolean)
    .join(' ');
  const profileTitle =
    data.keepNameSameAsRealName === false
      ? data.creatorName?.trim() || 'Creator Profile'
      : isAuthenticated && authenticatedUserName
        ? authenticatedUserName
        : applicantName || 'Creator Profile';
  const profileRole = isAuthenticated
    ? user?.roles.includes(roles.peerSupport)
      ? 'Peer Support'
      : 'Organization Admin'
    : '';

  return (
    <VStack
      minHeight={'100vh'}
      width={'100vw'}
      justifyContent={{ base: 'center', md: 'flex-start' }}
      paddingY={'130px'}
      paddingX={'24px'}
    >
      <Text variant={'loraTitle'} color={'white'} fontSize={'26px'} lineHeight={'32px'} fontWeight={'400'}>
        Preview your Creator Profile
      </Text>
      <Text
        variant={'urbanistSemiBoldRegular'}
        color={'white'}
        fontSize={{ base: '11px', md: '14px' }}
        lineHeight={{ base: '14px', md: '18px' }}
        textAlign={'center'}
        opacity={0.7}
        marginBottom={'40px'}
      >
        Make sure all the information is correct or go back to edit.
      </Text>

      <VStack
        width={'100%'}
        maxWidth={'640px'}
        backgroundColor={'white'}
        borderRadius={'12px'}
        boxShadow={'0px 16px 60px 0px #00000066'}
        alignItems={'stretch'}
        padding={{ base: '28px', md: '40px' }}
        spacing={'16px'}
      >
        <HStack alignItems={'center'} spacing={'22px'}>
          <Box
            width={'92px'}
            height={'92px'}
            borderRadius={'50%'}
            overflow={'hidden'}
            backgroundColor={primary[100]}
            flexShrink={0}
            boxShadow={'0px 2px 20px 0px #1A00B926'}
          >
            <Image
              src={(data.keepPhotoSameAsMemberPhoto ? memberPhoto : data.profilePhotoPreview) || svgFiles.genericUser}
              alt={'creator-profile-preview'}
              width={'100%'}
              height={'100%'}
              objectFit={'cover'}
            />
          </Box>
          <VStack alignItems={'flex-start'} spacing={'8px'}>
            <Text
              variant={'loraTitle'}
              color={'text.darkBlue'}
              fontSize={'26px'}
              lineHeight={'32px'}
              fontWeight={'400'}
            >
              {profileTitle}
            </Text>
            <Text
              variant={'loraTitle'}
              color={'text.mediumBlue'}
              fontSize={'16px'}
              lineHeight={'20px'}
              fontWeight={'400'}
            >
              {profileRole}
            </Text>
          </VStack>
        </HStack>

        <VStack alignItems={'flex-start'} spacing={'0px'}>
          <Text color={'background.blueGray'} fontSize={'10px'} lineHeight={'14px'} fontWeight={'800'}>
            ABOUT ME
          </Text>
          <Text color={'text.mediumBlue'} fontSize={'14px'} lineHeight={'19px'} fontWeight={'600'} maxWidth={'100%'}>
            {data.aboutMe}
          </Text>
        </VStack>

        <VStack alignItems={'flex-start'} spacing={'0px'}>
          <Text color={'background.blueGray'} fontSize={'10px'} lineHeight={'14px'} fontWeight={'800'}>
            CONTENT FOCUS
          </Text>
          <Text color={'text.mediumBlue'} fontSize={'14px'} lineHeight={'19px'} fontWeight={'600'}>
            {selectedContentFocusLabels}
          </Text>
        </VStack>

        {locationParts.length > 0 && (
          <VStack alignItems={'flex-start'} spacing={'0px'}>
            <Text color={'background.blueGray'} fontSize={'10px'} lineHeight={'14px'} fontWeight={'800'}>
              LOCATION
            </Text>
            <Text color={'text.mediumBlue'} fontSize={'14px'} lineHeight={'19px'} fontWeight={'600'}>
              {locationParts.join(', ')}
            </Text>
          </VStack>
        )}

        {socialLinks.length > 0 && (
          <VStack alignItems={'flex-start'} spacing={'0px'}>
            <Text color={'background.blueGray'} fontSize={'10px'} lineHeight={'14px'} fontWeight={'800'}>
              SOCIAL MEDIA HANDLES
            </Text>
            <VStack alignItems={'flex-start'} spacing={'16px'} width={'100%'} paddingTop={'6px'}>
              {socialLinks.map(({ platform, url }) => (
                <Link
                  key={platform}
                  href={url}
                  isExternal
                  display={'flex'}
                  alignItems={'center'}
                  gap={'12px'}
                  width={'100%'}
                  color={'text.darkBlue'}
                  _hover={{ textDecoration: 'none' }}
                >
                  <Image
                    src={externalAppLogos[platform]}
                    alt={`${socialPlatformLabels[platform]} logo`}
                    width={'32px'}
                    height={'32px'}
                    borderRadius={'7px'}
                    objectFit={'contain'}
                    flexShrink={0}
                  />
                  <VStack alignItems={'flex-start'} spacing={'1px'} minWidth={0}>
                    <Text
                      color={'background.blueGray'}
                      fontSize={'11px'}
                      lineHeight={'14px'}
                      fontWeight={'800'}
                      textTransform={'uppercase'}
                    >
                      {socialPlatformLabels[platform]}
                    </Text>
                    <Text
                      color={'text.mediumBlue'}
                      fontSize={'14px'}
                      lineHeight={'19px'}
                      fontWeight={'600'}
                      overflowWrap={'anywhere'}
                    >
                      {url}
                    </Text>
                  </VStack>
                </Link>
              ))}
            </VStack>
          </VStack>
        )}
      </VStack>

      <Text
        variant={'loraTitle'}
        color={'white'}
        fontSize={'18px'}
        lineHeight={'24px'}
        fontWeight={'400'}
        marginTop={'40px'}
        marginBottom={'16px'}
      >
        Everything looks good?
      </Text>
      <Button
        variant={'continue'}
        width={'100%'}
        maxWidth={'360px'}
        onClick={() => navigate({ to: '/creator/apply/content-policy' })}
      >
        Continue
      </Button>
    </VStack>
  );
};

export default CreatorApplicationProfilePreview;
